import {
  type CSSProperties,
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

/**
 * The reading surface shared by the signed-in reader (`/read/:gid`) and the share reader (`/r/:token`):
 * scroll or one page at a time, the turning direction, keys, wheel and taps, and a page that fails to load.
 * It knows nothing of accounts, routing or where the pages come from; the caller draws the bar.
 */
export type ReaderMode = "scroll" | "single";

export interface ReaderPageImage {
  index: number;
  url: string;
  width: number | null;
  height: number | null;
}

export interface ReaderBarState {
  barRef: RefObject<HTMLElement | null>;
  current: number;
  count: number;
  mode: ReaderMode;
  switchMode: () => void;
}

const MOBILE_READER = "(max-width: 640px), (pointer: coarse)";

/** Phones and touch screens fit pages to the width and keep their own turning direction. */
export function useMobileReader(): boolean {
  const [mobile, setMobile] = useState(() => window.matchMedia(MOBILE_READER).matches);
  useEffect(() => {
    const media = window.matchMedia(MOBILE_READER);
    const update = () => setMobile(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);
  return mobile;
}

/** A key the reader may act on: not typed into a field, not part of a shortcut with a modifier. */
export function isReaderKey(e: KeyboardEvent): boolean {
  if (e.altKey || e.ctrlKey || e.metaKey) return false;
  return !(
    e.target instanceof HTMLElement &&
    (e.target.isContentEditable || e.target.closest("input, select, textarea, [contenteditable]"))
  );
}

/**
 * One page. The box keeps the page's shape before the picture arrives; a picture that fails asks the caller
 * once (`onError` true = try the picture again) and otherwise leaves the number.
 */
function PageFigure({
  page,
  eager = false,
  onError,
}: {
  page: ReaderPageImage;
  eager?: boolean;
  onError: (index: number) => Promise<boolean>;
}) {
  const [imageState, setImageState] = useState<"initial" | "refreshing" | "retry" | "failed">("initial");
  const recovering = useRef(false);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  async function recover() {
    if (imageState === "retry") {
      setImageState("failed");
      return;
    }
    if (recovering.current) return;
    recovering.current = true;
    setImageState("refreshing");
    const renewed = await onError(page.index);
    if (alive.current) setImageState(renewed ? "retry" : "failed");
  }

  const ratio = page.width && page.height ? `${page.width} / ${page.height}` : "5 / 7";
  return (
    <figure
      className="rd-page"
      data-page={page.index}
      style={imageState === "refreshing" || imageState === "failed" ? { aspectRatio: ratio } : undefined}
    >
      <figcaption>
        {imageState === "failed" ? `${page.index}쪽을 불러오지 못했습니다` : `${page.index}쪽`}
      </figcaption>
      {(imageState === "initial" || imageState === "retry") && (
        <img
          key={imageState}
          src={page.url}
          alt={`${page.index}쪽`}
          loading={eager ? "eager" : "lazy"}
          decoding="async"
          style={{ aspectRatio: `auto ${ratio}` }}
          width={page.width ?? undefined}
          height={page.height ?? undefined}
          onError={recover}
        />
      )}
    </figure>
  );
}

export function ReaderView({
  pages,
  reverse,
  startPage,
  initialMode = "scroll",
  onModeChange,
  onPageError,
  onClose,
  renderBar,
  notice,
}: {
  /** at least one page */
  pages: readonly ReaderPageImage[];
  /** turning direction of this device; null while it is not known yet (the keys wait for it) */
  reverse: boolean | null;
  /** page to open at; a page past the end is ignored */
  startPage?: number;
  initialMode?: ReaderMode;
  onModeChange?: (mode: ReaderMode) => void;
  onPageError: (index: number) => Promise<boolean>;
  /** Escape; nothing happens without it */
  onClose?: () => void;
  renderBar: (bar: ReaderBarState) => ReactNode;
  /** shown under the bar */
  notice?: ReactNode;
}) {
  const mobile = useMobileReader();
  const flip = reverse ?? false;
  const barRef = useRef<HTMLElement>(null);
  const [barHeight, setBarHeight] = useState(0);
  useLayoutEffect(() => {
    const header = barRef.current;
    if (!header) return;
    const update = () => setBarHeight(header.getBoundingClientRect().height);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(header);
    return () => observer.disconnect();
  }, []);

  const [mode, setMode] = useState<ReaderMode>(initialMode);
  const [current, setCurrent] = useState(1);
  const count = pages.length;
  const strip = useRef<HTMLDivElement>(null);

  const startAt = useRef(startPage);
  useEffect(() => {
    const page = startAt.current;
    startAt.current = undefined;
    if (page === undefined || page > count) return;
    setCurrent(page);
    strip.current?.querySelector(`[data-page="${page}"]`)?.scrollIntoView({ block: "start" });
  }, [count]);

  const turn = useCallback(
    (step: number) => {
      setCurrent((n) => Math.min(Math.max(n + step, 1), Math.max(count, 1)));
      window.scrollTo({ top: 0, behavior: "instant" });
    },
    [count],
  );

  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!isReaderKey(e)) return;
      if (e.key === "Escape") {
        close.current?.();
        return;
      }
      if (e.key === " " && e.target instanceof HTMLElement && e.target.closest("button, a")) return;
      if (
        mode === "single" &&
        reverse !== null &&
        (e.key === "ArrowRight" || e.key === "ArrowLeft" || e.key === " ")
      ) {
        e.preventDefault();
        turn(e.key === " " ? 1 : (e.key === "ArrowRight" ? 1 : -1) * (reverse ? -1 : 1));
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [mode, turn, reverse]);

  const surface = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const root = surface.current;
    if (!root) return;
    const onWheel = (event: WheelEvent) => {
      if (
        !event.cancelable ||
        event.ctrlKey ||
        event.metaKey ||
        event.altKey ||
        event.shiftKey ||
        (window.visualViewport?.scale ?? 1) > 1 ||
        Math.abs(event.deltaX) > Math.abs(event.deltaY) ||
        event.deltaY === 0 ||
        !(event.target instanceof Element) ||
        !event.target.closest(".rd-page, .rd-single, .rd-strip")
      )
        return;
      const unit =
        event.deltaMode === 1
          ? Number.parseFloat(getComputedStyle(root).lineHeight) || 16
          : event.deltaMode === 2
            ? window.innerHeight
            : 1;
      event.preventDefault();
      window.scrollBy({ top: event.deltaY * unit * 1.8, behavior: "instant" });
    };
    root.addEventListener("wheel", onWheel, { passive: false });
    return () => root.removeEventListener("wheel", onWheel);
  }, []);

  // in scroll mode the counter follows the page in the middle of the window (where the browser can tell)
  // biome-ignore lint/correctness/useExhaustiveDependencies: `count` is when the pages to observe exist
  useEffect(() => {
    const root = strip.current;
    if (mode !== "scroll" || !root || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) setCurrent(Number((entry.target as HTMLElement).dataset.page));
        }
      },
      { rootMargin: "-50% 0px -50% 0px" },
    );
    for (const el of root.querySelectorAll(".rd-page")) observer.observe(el);
    return () => observer.disconnect();
  }, [mode, count]);

  function switchMode() {
    const next: ReaderMode = mode === "scroll" ? "single" : "scroll";
    setMode(next);
    onModeChange?.(next);
    if (next === "scroll") {
      const page = current;
      requestAnimationFrame(() =>
        strip.current?.querySelector(`[data-page="${page}"]`)?.scrollIntoView({ block: "start" }),
      );
    } else {
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  }

  const shown = pages[Math.min(current, count) - 1] as ReaderPageImage;
  return (
    <div
      className="rd"
      data-mode={mode}
      data-device={mobile ? "mobile" : "desktop"}
      style={{ "--rd-bar-height": `${barHeight}px` } as CSSProperties}
      ref={surface}
    >
      {renderBar({ barRef, current, count, mode, switchMode })}
      {notice}
      {mode === "scroll" ? (
        <div className="rd-strip" ref={strip}>
          {pages.map((page) => (
            <PageFigure key={page.index} page={page} eager={page.index <= 2} onError={onPageError} />
          ))}
        </div>
      ) : (
        <div className="rd-single">
          <PageFigure key={shown.index} page={shown} eager onError={onPageError} />
          <button
            type="button"
            className="rd-half rd-half-left"
            aria-label={flip ? "다음 쪽" : "앞 쪽"}
            disabled={flip ? current >= count : current <= 1}
            onClick={() => turn(flip ? 1 : -1)}
          />
          <button
            type="button"
            className="rd-half rd-half-right"
            aria-label={flip ? "앞 쪽" : "다음 쪽"}
            disabled={flip ? current <= 1 : current >= count}
            onClick={() => turn(flip ? -1 : 1)}
          />
        </div>
      )}
    </div>
  );
}
